import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import WhatYouToldUs from './components/problems';
import EmpathyScroller from "./components/Impacts";
import SolutionGuidedCalm from "./components/Solutions";
import FeaturesHorizontal from "./components/Features";
import Quotes from "./components/SocialProof";
import Price from "./components/Price";
import AISection from "./components/AISection";
import ProductShowcase from "./components/ProductShowcase";
import FAQ from "./components/FAQ";
import HeroI from "./screenshots/overview.png"
import logo from "./PrismDQ_logo.png"

import {
  Sparkles,
  ChevronRight,
  Moon,
  Sun,
  Menu,
} from "lucide-react";

// ---- Light theme palette (iOS19-inspired, blue-forward + glass) ----
const palette = {
  bg: "#F8FAFF",
  bgSoft: "#F2F6FF",
  text: "#0B1220",
  sub: "#41557A",
  blue: "#2563EB",
  blueLight: "#60A5FA",
  cyan: "#22D3EE",
  ring: "rgba(37, 99, 235, 0.15)",
  card: "rgba(255,255,255,0.75)",
  cardRing: "rgba(37,99,235,0.12)",
};

// --- Mini design system ---
const cn = (...c) => c.filter(Boolean).join(" ");

function Button({ as: As = "button", className = "", children, icon: Icon, style, ...props }) {
  return (
    <As
      className={cn(
        "group inline-flex items-center gap-2 rounded-2xl px-5 py-3 text-sm font-semibold text-white shadow-sm",
        "transition-all duration-300 backdrop-blur"
      )}
      style={{ backgroundColor: palette.blue, boxShadow: "0 6px 20px rgba(37,99,235,0.25)", border: `1px solid ${palette.ring}`, ...style }}
      {...props}
    >
      {children}
      {Icon ? (
        <Icon className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
      ) : null}
    </As>
  );
}

function GhostButton({ className = "", children, style, ...props }) {
  return (
    <button
      className={cn(
        "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-medium",
        "bg-white/60 hover:bg-white ring-1"
      )}
      style={{ color: palette.text + "CC", borderColor: palette.ring, backdropFilter: "blur(8px)", ...style }}
      {...props}
    >
      {children}
    </button>
  );
}

function Card({ className = "", children, style }) {
  return (
    <div
      className={cn(
        "rounded-3xl p-6",
        "shadow-[0_10px_30px_rgba(15,23,42,0.06)]"
      )}
      style={{ background: palette.card, border: `1px solid ${palette.cardRing}`, backdropFilter: "blur(12px)", ...style }}
    >
      {children}
    </div>
  );
}

function Chip({ children, style }) {
  return (
    <span
      className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium shadow-sm"
      style={{ border: "1px solid rgba(37,99,235,0.2)", background: "rgba(255,255,255,0.7)", color: "#1E3A8A", backdropFilter: "blur(8px)", ...style }}
    >
      {children}
    </span>
  );
}

function useTheme() {
  const [dark, setDark] = useState(false);
  useEffect(() => {
    const root = document.documentElement;
    if (dark) root.classList.add("dark");
    else root.classList.remove("dark");
  }, [dark]);
  return { dark, setDark };
}

const brand = "PrismDQ";

function Nav() {
  const { dark, setDark } = useTheme();
  const [open, setOpen] = useState(false);
  return (
    <div className="fixed inset-x-0 top-0 z-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mt-4 flex items-center justify-between rounded-2xl p-2 shadow-sm"
             style={{ background: "rgba(255,255,255,0.7)", border: `1px solid ${palette.ring}`, backdropFilter: "blur(10px)" }}>
          <div className="flex items-center gap-3">
            {/* <div className="h-9 w-9 rounded-xl ring-1" style={{ background: "linear-gradient(135deg,#93C5FD,#22D3EE)", borderColor: palette.ring }} /> */}
            <span className="text-sm font-semibold tracking-wide" style={{ color: palette.text }}>
               <img
                  src={logo}
                
                  alt="Dashboard screenshot"
                  className="h-10 w-auto" 
                />
            </span>
          </div>
          <div className="hidden items-center gap-6 md:flex">
            {[
              ["#solution", "Solution"],
              ["#features", "Features"],
              ["#product", "See it in action"],
              ["#ai-agents", "AI & Agents"],
              ["#pricing", "Pricing"],
            ].map(([href, label]) => (
              <a key={href} href={href} className="text-sm hover:opacity-90" style={{ color: palette.sub }}>
                {label}
              </a>
            ))}
            <div className="flex items-center gap-2">
              {/* <GhostButton onClick={() => setDark(!dark)}>
                {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                <span>{dark ? "Light" : "Dark"}</span>
              </GhostButton> */}
              <Button as="a" href="https://app.prismdq.com/" icon={ChevronRight}>Launch App</Button>
            </div>
          </div>
          <div className="md:hidden">
            <GhostButton onClick={() => setOpen((s) => !s)}>
              <Menu className="h-5 w-5" />
              Menu
            </GhostButton>
          </div>
        </div>
        {open && (
          <div className="mt-2 rounded-2xl p-2 shadow-sm md:hidden"
               style={{ background: "rgba(255,255,255,0.7)", border: `1px solid ${palette.ring}`, backdropFilter: "blur(10px)" }}>
            <div className="grid gap-1 p-2">
              {[
                ["#problem", "Problem"],
                ["#solution", "Solution"],
                ["#features", "Features"],
                ["#product", "See it in action"],
                ["#ai-agents", "AI & Agents"],
                ["#pricing", "Pricing"],
              ].map(([href, label]) => (
                <a key={href} href={href} className="rounded-xl px-3 py-2 text-sm hover:bg-blue-50" style={{ color: palette.sub }}>
                  {label}
                </a>
              ))}
              <div className="flex items-center justify-between gap-3 p-2">
                <GhostButton onClick={() => setDark((d) => !d)} className="flex-1 justify-center">
                  {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                  <span>{dark ? "Light" : "Dark"}</span>
                </GhostButton>
                <Button as="a" href="#cta" icon={ChevronRight} className="flex-1 justify-center">
                  Access
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section
      className="relative overflow-hidden pt-28"
      style={{
        background: `radial-gradient(60rem 30rem at 50% -10%, rgba(96,165,250,.20), transparent), linear-gradient(180deg, ${palette.bg}, ${palette.bgSoft})`,
      }}
    >
      {/* Soft blue orbs */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -left-24 -top-24 h-96 w-96 rounded-full bg-[radial-gradient(circle_at_30%_30%,rgba(96,165,250,.25),transparent)] blur-2xl" />
        <div className="absolute -right-24 -top-12 h-[28rem] w-[28rem] rounded-full bg-[radial-gradient(circle_at_70%_30%,rgba(34,211,238,.18),transparent)] blur-2xl" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-4xl text-center"
        >
          <Chip>
            <Sparkles className="h-3.5 w-3.5" /> Data quality, migration &amp; AI-ready analysis
          </Chip>

          <h1
            className="mt-5 text-balance text-4xl font-semibold tracking-tight sm:text-6xl"
            style={{ color: palette.text }}
          >
            Clean data.{" "}
            <span className="bg-gradient-to-r from-[#2563EB] via-[#60A5FA] to-[#22D3EE] bg-clip-text text-transparent">
              Confident migrations.
            </span>{" "}
            Data your AI can actually use.
          </h1>

          <p
            className="mx-auto mt-5 max-w-2xl text-balance text-base leading-7"
            style={{ color: palette.sub }}
          >
            PrismDQ profiles, cleans, and governs your data — then moves it from source to
            target with a quality gate at every stage. Analyse it, ask an AI assistant about
            it directly in plain English with no export to Excel, or expose it securely to
            your own AI agents over MCP. Built for any organisation that wants data — and
            AI — it can actually trust.
          </p>

          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button
              style={{ margin: "10px" }}
              as="a"
              href="https://app.prismdq.com/dashboard"
              icon={ChevronRight}
            >
              Try Free
            </Button>
            {/* <GhostButton as="a" href="#preview">Watch a 60-second tour</GhostButton> */}
          </div>

          {/* Visual placeholder */}
          <Card className="mt-12 p-0">
            <div
              className="relative w-full overflow-hidden rounded-3xl
                  aspect-[16/9] sm:aspect-[21/9]"
            >
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(800px_200px_at_50%_20%,rgba(96,165,250,.18),transparent)]" />
              <div className="absolute inset-0 flex items-center justify-center">
                <img
                  src={HeroI}
                  alt="PrismDQ quality dashboard showing data quality scores, dimension breakdown, and migration progress"
                  width="1680"
                  height="1050"
                  fetchPriority="high"
                  className="h-full w-full object-contain"
                />
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </section>
  );
}

function Problem() {
  return (
     <WhatYouToldUs />

  );
}

function Impacts(){
   return (
    <>
      {/* your quotes section */}
      <EmpathyScroller
       steps={[
  {
    headline: "Critical data issues discovered too late",
    sub: "In almost every migration, hidden errors surface only during testing—forcing teams into emergency clean-ups and pushing timelines out by weeks.",
    impact: "Typical impact: 20–30% project delay",
    tone: "down",
    gradient: "from-blue-100/70 via-white to-white",
  },
  {
    headline: "Teams drowning in manual data clean-up",
    sub: "Project analysts and SMEs get pulled away from their real jobs to fix duplicates, nulls, mismatches and legacy quirks—burning time and morale.",
    impact: "Typical impact: 40–60% of migration effort wasted on rework",
    tone: "down",
    gradient: "from-cyan-100/70 via-white to-white",
  },
  {
    headline: "UAT fails because migrated data doesn’t match expectations",
    sub: "The business rejects test cycles when fields don’t align, values look wrong, or records vanish. Confidence drops fast—and recovery is expensive.",
    impact: "Typical impact: $250k+ in added project cost",
    tone: "down",
    gradient: "from-indigo-100/70 via-white to-white",
  },
]}
      />
    </>
  );
}

function Solution() {
  return (
      <SolutionGuidedCalm />
  
  );
}

function Features() {
 
  return(
    <FeaturesHorizontal />
  )
}

function SocialProof() {
  return <Quotes />;
}

function Pricing() {
 return(
  <Price />
 );
}

function CTA() {
  return (
    <section id="cta" className="relative py-20" style={{ background: `linear-gradient(180deg, ${palette.bg}, ${palette.bgSoft})` }}>
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <Card className="p-8">
          <div className="grid items-center gap-8 md:grid-cols-2">
            <div>
              <h3 className="text-2xl font-semibold" style={{ color: palette.text }}>Get early access</h3>
              <p className="mt-2 text-sm" style={{ color: palette.sub }}>We’ll invite you as soon as we open the private beta.</p>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                alert("Thanks! We’ll be in touch soon.");
              }}
              className="flex w-full items-center gap-3"
            >
              <input
                className="w-full rounded-2xl px-4 py-3 text-sm"
                style={{ border: `1px solid ${palette.ring}`, background: "rgba(255,255,255,0.8)", color: palette.text }}
                placeholder="you@company.com"
                type="email"
                required
              />
              <Button type="submit" icon={ChevronRight}>Join</Button>
            </form>
          </div>
        </Card>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="relative py-12" style={{ background: palette.bg, borderTop: `1px solid ${palette.ring}` }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center justify-between gap-6 sm:flex-row">
          <div className="flex items-center gap-3">
           <img
                  src={logo}
                
                  alt="Dashboard screenshot"
                  className="h-10 w-auto" 
                />
            <span className="text-sm font-semibold" style={{ color: palette.text + "CC" }}></span>
          </div>
          <nav className="flex items-center gap-5 text-sm" style={{ color: palette.sub }}>
            <a href="#problem" className="hover:opacity-90">Problem</a>
            <a href="#solution" className="hover:opacity-90">Solution</a>
            <a href="#features" className="hover:opacity-90">Features</a>
            <a href="#product" className="hover:opacity-90">See it in action</a>
            <a href="#ai-agents" className="hover:opacity-90">AI &amp; Agents</a>
            <a href="#pricing" className="hover:opacity-90">Pricing</a>
            <a href="#" className="hover:opacity-90">Privacy</a>
          </nav>
          <div className="text-xs" style={{ color: palette.sub }}>© {new Date().getFullYear()} {brand}</div>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <div className="min-h-screen" style={{ background: palette.bg }}>
      <Nav />
      <main>
        <Hero />
        <Problem />
        <Impacts />
        <Solution />
        <AISection />
        <Features />
        <ProductShowcase />
        <SocialProof />
        <Pricing />
        <FAQ />
        <CTA />
      </main>
      <Footer />
    </div>
  );
}
 